/**
 * LandingPagesPage — Listing page for LP assets
 *
 * Structure matches FunnelsPage exactly:
 *   - Controls row: Views dropdown, search, filter toggle, columns, + New LP
 *   - Filter card (left) + List card (right) grid
 *   - Table with sortable columns, row checkboxes, pagination
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { useHasModule } from "@/hooks/usePermissions";
import { getStoredUser } from "@/lib/auth";
import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import InCardFilterPanel from "@/components/leads/InCardFilterPanel";
import LeadTableColumnsDialog from "@/components/leads/LeadTableColumnsDialog";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

import {
  ArrowUpDown,
  ChevronDown,
  Download,
  ListFilter,
  Loader2,
  Plus,
  Settings2,
  Trash2,
  Layers,
  Check,
  Sparkles,
} from "lucide-react";

// ====== HELPERS ======

function prettifyLabel(key) {
  const map = {
    name: "Name",
    status: "Status",
    primary_use_case: "Use Case",
    current_version_id: "Version",
    updated_at: "Updated",
    created_at: "Created",
  };
  return map[key] || key;
}

function toCsvCell(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return '"' + s.replaceAll('"', '""') + '"';
  }
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(toCsvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function getRowField(row, key) {
  if (!row) return "";
  if (key === "updated_at" || key === "created_at") {
    const raw = row[key];
    if (!raw) return "";
    return format(new Date(raw), "MMM d, yyyy");
  }
  if (key === "primary_use_case") {
    return row.metadata?.primary_use_case?.replace(/_/g, " ") || "";
  }
  if (key === "current_version_id") {
    return row.current_version_id ? "Has version" : "No version";
  }
  return row[key] ?? "";
}

// ====== CHIP / BADGES ======

const CHIP_CLASSES = [
  "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20",
  "bg-emerald-500/20 text-emerald-100 ring-emerald-400/20",
  "bg-cyan-500/20 text-cyan-100 ring-cyan-400/20",
  "bg-fuchsia-500/20 text-fuchsia-100 ring-fuchsia-400/20",
  "bg-amber-500/20 text-amber-100 ring-amber-400/20",
];

function hashString(s) {
  let h = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function Chip({ value }) {
  const v = String(value || "\u2014");
  const cls = CHIP_CLASSES[hashString(v) % CHIP_CLASSES.length];
  return (
    <Badge className={`ring-1 max-w-full overflow-hidden whitespace-nowrap ${cls}`}>
      <span className="min-w-0 truncate">{v}</span>
    </Badge>
  );
}

function StatusBadge({ status }) {
  if (status === "active" || status === "published") {
    return <Badge className="bg-green-500/10 text-green-400 hover:bg-green-500/20 ring-1 ring-green-400/20">Active</Badge>;
  }
  if (status === "archived") {
    return <Badge className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 ring-1 ring-amber-400/20">Archived</Badge>;
  }
  return <Badge variant="outline" className="text-slate-200/60">Draft</Badge>;
}

function renderCell(row, key) {
  if (key === "status") return <StatusBadge status={row.status} />;
  if (key === "primary_use_case") {
    const uc = row.metadata?.primary_use_case;
    if (!uc) return <span className="text-slate-500">\u2014</span>;
    return <Chip value={uc.replace(/_/g, " ")} />;
  }
  return String(getRowField(row, key) ?? "");
}

// ====== CONSTANTS ======

const DEFAULT_TABLE_COLUMNS = ["name", "status", "primary_use_case", "updated_at", "created_at"];
const DEFAULT_VISIBLE_FILTERS = ["name", "status"];
const VIEW_LABEL = "All Landing Pages";
const DEFAULT_LIMIT = 10;

// ====== MAIN COMPONENT ======

export default function LandingPagesPage() {
  usePageTitle("Landing Pages");
  const { toast } = useToast();
  const nav = useNavigate();
  const user = getStoredUser();
  const { selectedTenantId } = useTenant();

  // MA-PHASE10: Use module-based permissions
  const { hasAccess: canManageLandingPages } = useHasModule("landing_pages");

  const [selected, setSelected] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("lp_filters_open");
      return stored !== null ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try { localStorage.setItem("lp_filters_open", String(filtersOpen)); } catch {}
  }, [filtersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState(DEFAULT_TABLE_COLUMNS);

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // ── Fetch ──
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/landing-pages/list", {
        params: { limit: 200, skip: 0 },
      });
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Filter options ──
  const allStatuses = useMemo(() => [...new Set(items.map((r) => r.status).filter(Boolean))], [items]);
  const allUseCases = useMemo(() => [...new Set(items.map((r) => r.metadata?.primary_use_case).filter(Boolean))], [items]);

  const filterFields = useMemo(() => [
    { key: "name", label: "Name", type: "text" },
    { key: "status", label: "Status", type: "select", options: allStatuses },
    { key: "primary_use_case", label: "Use Case", type: "select", options: allUseCases.map((u) => u.replace(/_/g, " ")) },
  ], [allStatuses, allUseCases]);

  const optionsByField = useMemo(() => ({
    status: allStatuses,
    primary_use_case: allUseCases.map((u) => u.replace(/_/g, " ")),
  }), [allStatuses, allUseCases]);

  const filtersActive = Object.values(filtersApplied).some((v) => v !== undefined && v !== null && v !== "");

  const clearFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
  };

  // ── Filtering + sorting ──
  const filteredRows = useMemo(() => {
    let rows = [...items];

    // Search
    if (qDebounced) {
      const lower = qDebounced.toLowerCase();
      rows = rows.filter((r) => (r.name || "").toLowerCase().includes(lower));
    }

    // Applied filters
    Object.entries(filtersApplied).forEach(([key, val]) => {
      if (!val) return;
      const lower = String(val).toLowerCase();
      if (key === "name") {
        rows = rows.filter((r) => (r.name || "").toLowerCase().includes(lower));
      } else if (key === "status") {
        rows = rows.filter((r) => (r.status || "").toLowerCase() === lower);
      } else if (key === "primary_use_case") {
        rows = rows.filter((r) => (r.metadata?.primary_use_case || "").replace(/_/g, " ").toLowerCase() === lower);
      }
    });

    return rows;
  }, [items, qDebounced, filtersApplied]);

  const total = filteredRows.length;

  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const va = getRowField(a, sortBy) || "";
      const vb = getRowField(b, sortBy) || "";
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [filteredRows, sortBy, sortDir]);

  const pageRows = sortedRows.slice(offset, offset + limit);

  const applySort = (col, dir) => {
    setSortBy(col);
    setSortDir(dir);
  };

  // ── Selection ──
  const anySelected = selected.size > 0;
  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const selectedRows = items.filter((r) => selected.has(r.id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageRows.forEach((r) => next.delete(r.id));
      } else {
        pageRows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  // ── Actions ──
  const exportSelectedCsv = () => {
    if (!anySelected) return;
    const fields = tableColumns;
    const rows = [fields.map(prettifyLabel)];
    selectedRows.forEach((r) => rows.push(fields.map((k) => getRowField(r, k) ?? "")));
    downloadCsv(`landing_pages_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast({ title: "Export downloaded" });
  };

  const deleteSelected = async () => {
    try {
      await Promise.all(selectedRows.map((r) => api.delete(`/landing-pages/${r.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  const handleEdit = (lp) => {
    nav(`/app/landing-pages/${lp.id}`);
  };

  const handleDelete = async (lp) => {
    if (!window.confirm(`Delete "${lp.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/landing-pages/${lp.id}`);
      toast({ title: "Landing page deleted" });
      refresh();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  const [creating, setCreating] = useState(false);

  const handleNew = async () => {
    setCreating(true);
    try {
      const res = await api.post("/landing-pages/register", {
        name: `New Landing Page — ${format(new Date(), "MMM d, yyyy h:mm a")}`,
      });
      const newLp = res.data;
      toast({ title: "Landing Page Created", description: `"${newLp.name}" is ready to edit.` });
      nav(`/app/landing-pages/${newLp.id}`);
    } catch (e) {
      toast({ title: "Create Failed", description: e?.response?.data?.detail || "Could not create landing page.", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  // ── Column min-width ──
  const tableMinWidthPx = `${(tableColumns.length * 160) + 46 + 44}px`;

  // ====== RENDER ======

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="landing-pages-page">
      {/* Controls row */}
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="lp-view"
          >
            {VIEW_LABEL}
            <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
          </Button>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="lp-actions">
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem
                  onClick={exportSelectedCsv}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid="lp-export-selected"
                >
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={deleteSelected}
                  className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                  data-testid="lp-delete-selected"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <CollapsibleSearch
            value={q}
            onChange={setQ}
            placeholder="Search landing pages..."
            width="w-[280px]"
            data-testid="lp-search"
          />

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-label="Open filters"
            data-testid="lp-filter-icon"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : ""}`} />
            {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" /> : null}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setColumnsOpen(true)}
            aria-label="Customize columns"
            data-testid="lp-customize-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          {/* MA-RBAC-LANDINGPAGES-FULL-USER-UI-DENIED-122: Hide New button if user lacks landing_pages module */}
          {canManageLandingPages && (
            <Button onClick={handleNew} disabled={creating} data-testid="create-lp-btn">
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} New Landing Page
            </Button>
          )}
        </div>
      </div>

      {/* Filter + List grid */}
      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (filtersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {filtersOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="lp-filter-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                title="Filter Landing Pages by"
                fields={filterFields}
                optionsByField={optionsByField}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={() => setFiltersApplied(filtersDraft)}
                onClear={clearFilters}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="lp-list-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading...
              </div>
            ) : (
              <div data-testid="lp-table-wrap" className="flex-1 min-w-0 overflow-hidden">
                <div
                  className="h-full w-full min-w-0 overflow-auto"
                  data-testid="lp-hscroll"
                  onWheel={(e) => {
                    if (e.shiftKey) {
                      e.preventDefault();
                      e.currentTarget.scrollLeft += e.deltaY;
                    }
                  }}
                >
                  <Table style={{ minWidth: tableMinWidthPx }} containerClassName="h-full">
                    <TableHeader className="relative">
                      <TableRow className="border-transparent hover:bg-transparent">
                        <TableHead colSpan={(tableColumns || []).length + 1} className="relative h-auto p-1 text-slate-200/70">
                          <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-[11px] bg-white/10" />
                          <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                            <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={allOnPageSelected}
                                onCheckedChange={toggleAllOnPage}
                                className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                data-testid="lp-select-all"
                              />
                            </div>
                            <div
                              className="min-w-0 grid items-center gap-3 pr-2"
                              style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                              data-testid="lp-header-grid"
                            >
                              {(tableColumns || []).map((k) => (
                                <div key={k} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                                  <span className="min-w-0 flex-1 truncate">{prettifyLabel(k)}</span>
                                  <button
                                    type="button"
                                    className={
                                      "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                      (sortBy === k ? " bg-white/10" : "")
                                    }
                                    aria-label={`Sort ${prettifyLabel(k)}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const nextDir = sortBy === k && sortDir === "asc" ? "desc" : "asc";
                                      applySort(k, nextDir);
                                    }}
                                    data-testid={`lp-sort-${k}`}
                                  >
                                    <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="h-9 w-9" />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {sortedRows.length === 0 ? (
                        <TableRow className="border-transparent">
                          <TableCell colSpan={(tableColumns || []).length + 1} className="py-16">
                            <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                              <Layers className="h-12 w-12 text-indigo-400 mb-3" />
                              <div className="text-sm text-slate-200/70">No landing pages found.</div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : pageRows.map((row) => (
                        <TableRow key={row.id} className="border-transparent" data-testid={`lp-row-${row.id}`}>
                          <TableCell colSpan={(tableColumns || []).length + 1} className="p-1 align-top">
                            <div
                              role="button"
                              tabIndex={0}
                              className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                              onClick={() => handleEdit(row)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleEdit(row);
                                }
                              }}
                              data-testid={`lp-row-button-${row.id}`}
                            >
                              <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selected.has(row.id)}
                                    onCheckedChange={() => toggleOne(row.id)}
                                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                    data-testid={`lp-row-select-${row.id}`}
                                  />
                                </div>
                                <div className="min-w-0 grid grid-cols-1">
                                  <div
                                    className="grid items-center gap-3 pr-2"
                                    style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                                    data-testid="lp-row-grid"
                                  >
                                    {(tableColumns || []).map((k) => (
                                      <div key={k} className="min-w-0">
                                        {k === "status" || k === "primary_use_case" ? (
                                          renderCell(row, k)
                                        ) : k === "name" ? (
                                          <div>
                                            <div className="truncate text-slate-100/90 font-medium">{row.name}</div>
                                            <div className="text-xs text-slate-200/60">{row.id}</div>
                                          </div>
                                        ) : (
                                          <div className="truncate text-slate-100/90">{renderCell(row, k)}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <ChevronDown className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="glass border-white/10 text-slate-100">
                                      {/* MA-RBAC-LANDINGPAGES-FULL-USER-UI-DENIED-122: Hide Edit/Delete if user lacks landing_pages module */}
                                      {canManageLandingPages && (
                                        <>
                                          <DropdownMenuItem
                                            onClick={() => handleEdit(row)}
                                            className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                          >
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDelete(row)}
                                            className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div data-testid="lp-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70" data-testid="lp-count">
                {anySelected ? `Selected: ${selected.size}` : `Matching current filters: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="lp-pagination">
                {offset > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    data-testid="lp-prev"
                  >
                    Previous
                  </Button>
                ) : null}

                {offset + limit < total ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setOffset((o) => o + limit)}
                    data-testid="lp-next"
                  >
                    Next
                  </Button>
                ) : null}

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setOffset(0);
                  }}
                >
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20" data-testid="lp-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`lp-page-size-${n}`}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Columns dialog */}
      <LeadTableColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        fields={[
          { key: "name", label: "Name" },
          { key: "status", label: "Status" },
          { key: "primary_use_case", label: "Use Case" },
          { key: "updated_at", label: "Updated" },
          { key: "created_at", label: "Created" },
        ]}
        value={tableColumns}
        onSave={setTableColumns}
      />
    </div>
  );
}
